import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, peso, formatApiErrorDetail } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { CustomerLayout } from "@/components/CustomerLayout";
import { fmtAddress } from "@/components/OrderTicket";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Truck, Store } from "lucide-react";
import { toast } from "sonner";

export default function Checkout() {
  const nav = useNavigate();
  const { items, subtotal, clear } = useCart();
  const { user } = useAuth();
  const [branch, setBranch] = useState(null);
  const [orderType, setOrderType] = useState("delivery");
  const [payment, setPayment] = useState("cod");
  const [methods, setMethods] = useState([]);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const idemKey = useRef(crypto.randomUUID());

  useEffect(() => {
    api.get("/branches").then((r) => setBranch(r.data[0]));
    api.get("/settings/payment-methods").then((r) => setMethods(r.data.filter((m) => m.active)));
  }, []);

  const deliveryFee = orderType === "delivery" && branch ? (branch.free_delivery_threshold && subtotal >= branch.free_delivery_threshold ? 0 : branch.delivery_fee) : 0;
  const total = subtotal + deliveryFee;

  if (items.length === 0) {
    return <CustomerLayout><div className="max-w-2xl mx-auto px-4 py-20 text-center"><p className="text-muted-foreground">Your cart is empty.</p><Button className="mt-4 bg-[#1B5E20]" onClick={() => nav("/")} data-testid="empty-back-btn">Browse Menu</Button></div></CustomerLayout>;
  }

  const placeOrder = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/orders", {
        idempotency_key: idemKey.current,
        items: items.map((i) => ({ product_id: i.product.id, quantity: i.quantity })),
        order_type: orderType, payment_method: payment, delivery_notes: notes,
        branch_id: branch?.id, address: user.address,
        contact_name: user.name, contact_phone: user.phone,
      });
      clear();
      toast.success("Order placed successfully!");
      nav(`/order/${data.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setBusy(false);
    }
  };

  return (
    <CustomerLayout>
      <div className="max-w-2xl mx-auto px-4 py-8">
        <h1 className="font-display text-3xl font-bold mb-6">Checkout</h1>

        <div className="bg-white rounded-2xl border p-5 mb-4">
          <h2 className="font-semibold mb-3">Order Type</h2>
          <RadioGroup value={orderType} onValueChange={setOrderType} className="grid grid-cols-2 gap-3">
            <label className={`flex items-center gap-3 border rounded-xl p-4 cursor-pointer ${orderType === "delivery" ? "border-[#1B5E20] bg-[#E8F5E9]" : ""}`} data-testid="type-delivery">
              <RadioGroupItem value="delivery" /> <Truck size={20} className="text-[#1B5E20]" /> <span className="font-medium">Delivery</span>
            </label>
            <label className={`flex items-center gap-3 border rounded-xl p-4 cursor-pointer ${orderType === "pickup" ? "border-[#1B5E20] bg-[#E8F5E9]" : ""}`} data-testid="type-pickup">
              <RadioGroupItem value="pickup" /> <Store size={20} className="text-[#1B5E20]" /> <span className="font-medium">Pick-Up</span>
            </label>
          </RadioGroup>
        </div>

        <div className="bg-white rounded-2xl border p-5 mb-4">
          <h2 className="font-semibold mb-1">{orderType === "delivery" ? "Delivery Address" : "Pick-Up At"}</h2>
          {orderType === "delivery" ? (
            <div className="text-sm">
              <div className="font-medium">{user.name} · {user.phone}</div>
              <div className="text-muted-foreground mt-1">{user.address ? fmtAddress(user.address) : "No saved address — please update in My Account."}</div>
              <button onClick={() => nav("/account")} className="text-[#1B5E20] text-sm font-semibold mt-2" data-testid="edit-address-btn">Edit address</button>
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">{branch?.name} — {branch?.subdivision}, Brgy. {branch?.barangay}, {branch?.city}</div>
          )}
        </div>

        <div className="bg-white rounded-2xl border p-5 mb-4">
          <h2 className="font-semibold mb-3">Payment Method</h2>
          <RadioGroup value={payment} onValueChange={setPayment} className="space-y-2">
            {methods.map((m) => (
              <label key={m.code} className={`flex items-center gap-3 border rounded-xl p-3 cursor-pointer ${payment === m.code ? "border-[#1B5E20] bg-[#E8F5E9]" : ""}`} data-testid={`pay-${m.code}`}>
                <RadioGroupItem value={m.code} /> <span className="font-medium">{m.name}</span>
              </label>
            ))}
          </RadioGroup>
        </div>

        <div className="bg-white rounded-2xl border p-5 mb-4">
          <Label className="font-semibold">Delivery Instructions</Label>
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="mt-2" placeholder="e.g. Please call upon arrival" data-testid="checkout-notes" />
        </div>

        <div className="bg-white rounded-2xl border p-5 mb-4">
          <h2 className="font-semibold mb-3">Order Summary</h2>
          {items.map((i) => (
            <div key={i.product.id} className="flex justify-between text-sm py-1">
              <span>{i.quantity}× {i.product.name}</span><span>{peso(i.product.price * i.quantity)}</span>
            </div>
          ))}
          <div className="border-t mt-3 pt-3 space-y-1.5 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span data-testid="sum-subtotal">{peso(subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Delivery Fee</span><span data-testid="sum-delivery">{peso(deliveryFee)}</span></div>
            <div className="flex justify-between font-display font-extrabold text-lg text-[#1B5E20]"><span>TOTAL</span><span data-testid="sum-total">{peso(total)}</span></div>
          </div>
        </div>

        <Button disabled={busy || !branch || (orderType === "delivery" && !user.address)} onClick={placeOrder} data-testid="place-order-btn"
          className="w-full h-14 bg-[#C62828] hover:bg-[#B71C1C] text-base font-bold rounded-xl">
          {busy ? "Placing your order…" : `Confirm Order · ${peso(total)}`}
        </Button>
      </div>
    </CustomerLayout>
  );
}
